import React, { useContext, useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import SidebarContext from "../../contexts/MyContext";
import { errorToast, successToast } from "../../utils/notificationUtils";
import { truncateFileName } from "../../utils/truncateFileName";
import { AudioHandler } from "../../Apihander/AudioRecording/AudioRecording";
import { AUTO_DETECT, TRANSLATE_LANGUAGES, getLanguageLabel, translateText } from "../../utils/translate";
import "../../css/app/aiResults.css";

// Lets role="button" images respond to Enter/Space like real buttons.
const activateOnKey = (e) => { if (e.key === "Enter" || e.key === " ") e.currentTarget.click(); };

const TranslateTextLogic = () => {
    const { translateSource, setTranslateSource, setActionTabIndex } = useContext(SidebarContext);

    const resultRef = useRef(null);
    const [sourceLanguage, setSourceLanguage] = useState(AUTO_DETECT);
    const [targetLanguage, setTargetLanguage] = useState(translateSource?.targetLanguage || "en");
    const [translatedText, setTranslatedText] = useState("");
    const [detectedLanguage, setDetectedLanguage] = useState(null);
    const [providerLabel, setProviderLabel] = useState("");
    const [statusMessage, setStatusMessage] = useState("");
    const [isTranslating, setIsTranslating] = useState(false);

    const sourceText = translateSource?.text || "";
    const fileName = translateSource?.fileName || "";
    const audioId = translateSource?.audioId;

    // A different file is a different translation, so nothing from the last one is left on screen.
    useEffect(() => {
        setTranslatedText("");
        setDetectedLanguage(null);
        setProviderLabel("");
        setStatusMessage("");
    }, [translateSource?.text]);

    const handleClose = () => {
        setTranslateSource(null);
        setActionTabIndex(null);
    };

    // Stored on the recording so the translation can be fetched again later.
    const saveTranslation = async (text) => {
        if (!audioId || !text?.trim()) return;
        const response = await AudioHandler.doSaveTranslation(audioId, { languageCode: targetLanguage, translatedText: text });
        if (response?.statusCode !== 200) {
            errorToast(response?.response?.data?.message || "We couldn't save this translation. Please try again.");
        }
    };

    const handleTranslate = async () => {
        if (isTranslating || !sourceText.trim()) return;

        setIsTranslating(true);
        setTranslatedText("");
        setStatusMessage("Translating...");

        try {
            const result = await translateText({
                text: sourceText,
                sourceLanguage,
                targetLanguage,
                onStatus: setStatusMessage,
            });

            setTranslatedText(result.text);
            setDetectedLanguage(result.detectedLanguage);
            setProviderLabel(result.providerLabel);
            successToast(`Translated to ${getLanguageLabel(targetLanguage)}`);
            saveTranslation(result.text);
        } catch (error) {
            errorToast(error?.message || "We couldn't translate this text right now. Please try again.");
        } finally {
            setIsTranslating(false);
            setStatusMessage("");
        }
    };

    // AI Tools has already picked the language, so the translation starts straight away.
    const autoStartedRef = useRef(false);
    useEffect(() => {
        if (!translateSource?.autoTranslate || !sourceText || autoStartedRef.current) return;
        autoStartedRef.current = true;
        setTranslateSource((prev) => (prev ? { ...prev, autoTranslate: false } : prev));
        handleTranslate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translateSource?.autoTranslate, sourceText]);

    const handleCopy = () => {
        if (!resultRef.current) return;
        resultRef.current.select();
        document.execCommand("copy");
        successToast("Translation copied");
    };

    const handleSaveTextFile = () => {
        const blob = new Blob([translatedText], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        const baseName = fileName.replace(/\.[^.]+$/, "") || "translation";
        anchor.href = url;
        anchor.download = `${baseName}-${targetLanguage}.txt`;
        anchor.click();
        URL.revokeObjectURL(url);
    };

    if (!sourceText) {
        return (
            <div className="vr-air vr-tone-sky">
                <div className="vr-air-empty">
                    <div className="vr-icon-tile vr-air-empty-tile">
                        <Icon icon="lucide:languages" width="30" height="30" />
                    </div>
                    <h3 className="vr-air-empty-title">
                        Pick a recording to translate
                    </h3>
                    <p className="vr-air-empty-text">
                        Pick the Translate card in AI Tools and choose a recording or audio file to get started.
                    </p>
                    <button
                        className="vr-btn vr-btn-secondary"
                        onClick={handleClose}
                    >
                        <Icon icon="lucide:chevron-left" width="16" height="16" />
                        <span>Back to AI Tools</span>
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="vr-air vr-tone-sky">
            <header className="vr-air-head">
                <div className="vr-icon-tile is-solid vr-air-head-tile">
                    <Icon icon="lucide:languages" width="20" height="20" />
                </div>
                <div className="vr-air-head-text">
                    <h6 className="vr-air-title">
                        Translate
                    </h6>
                    <div className="vr-air-meta">
                        <Icon icon="lucide:file-text" width="15" height="15" className="vr-air-meta-icon" />
                        <h6 className="vr-air-file">
                            {truncateFileName(fileName, 35)}
                        </h6>
                    </div>
                </div>
                <img
                    src="./img/cancelIcon.png"
                    height={24}
                    width={24}
                    onClick={handleClose}
                    className="vr-air-close"
                    alt="Close"
                    role="button"
                    tabIndex={0}
                    onKeyDown={activateOnKey}
                />
            </header>

            <section className="vr-air-langbar">
                <div className="vr-air-field">
                    <label className="vr-air-field-label" htmlFor="translate-source-language">Translate from</label>
                    <select
                        id="translate-source-language"
                        className="vr-air-select"
                        value={sourceLanguage}
                        onChange={(e) => setSourceLanguage(e.target.value)}
                        disabled={isTranslating}
                    >
                        <option value={AUTO_DETECT}>Detect language</option>
                        {TRANSLATE_LANGUAGES.map((language) => (
                            <option key={language.code} value={language.code}>{language.label}</option>
                        ))}
                    </select>
                </div>

                <span className="vr-air-swap"><Icon icon="lucide:arrow-right" width="18" height="18" /></span>

                <div className="vr-air-field">
                    <label className="vr-air-field-label" htmlFor="translate-target-language">Translate to</label>
                    <select
                        id="translate-target-language"
                        className="vr-air-select"
                        value={targetLanguage}
                        onChange={(e) => setTargetLanguage(e.target.value)}
                        disabled={isTranslating}
                    >
                        {TRANSLATE_LANGUAGES.map((language) => (
                            <option key={language.code} value={language.code}>{language.label}</option>
                        ))}
                    </select>
                </div>

                <button
                    className="vr-btn vr-btn-primary vr-air-go"
                    onClick={handleTranslate}
                    disabled={isTranslating}
                >
                    {isTranslating ? (
                        <span className="vr-spinner vr-air-spinner-light" role="status" />
                    ) : (
                        <Icon icon="lucide:languages" width="16" height="16" />
                    )}
                    {isTranslating ? "Translating" : "Translate"}
                </button>

                {isTranslating && <div className="vr-progress is-indeterminate vr-air-progress"><i /></div>}
                {(statusMessage || providerLabel) && (
                    <p className="vr-air-langbar-status">
                        {statusMessage || `Translated with ${providerLabel}`}
                        {!statusMessage && detectedLanguage ? ` - detected ${getLanguageLabel(detectedLanguage)}` : ""}
                    </p>
                )}
            </section>

            <section className="vr-air-doc is-translate">
                <div className="vr-air-panes">
                    <div className="vr-air-pane-col">
                        <h1 className="vr-air-pane-title">Original text</h1>
                        <div className="vr-air-pane is-source">
                            <textarea
                                readOnly
                                className="form-control input-field scroll vr-air-text"
                                value={sourceText}
                            />
                        </div>
                    </div>

                    <div className="vr-air-pane-col">
                        <h1 className="vr-air-pane-title">
                            {getLanguageLabel(targetLanguage)}
                        </h1>
                        <div className="vr-air-pane is-result">
                            <textarea
                                className="form-control input-field scroll vr-air-text"
                                placeholder="Pick a language and press Translate."
                                ref={resultRef}
                                value={translatedText}
                                onChange={(e) => setTranslatedText(e.target.value)}
                            />
                            <div className="vr-air-pane-tools">
                                <img
                                    src="/img/transcript/copy.png"
                                    alt="Copy"
                                    title="Copy"
                                    onClick={handleCopy}
                                    className="vr-air-pane-icon"
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={activateOnKey}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="vr-air-actions is-end">
                    <button
                        className="vr-btn vr-btn-secondary"
                        onClick={handleCopy}
                        disabled={!translatedText}
                    >
                        <Icon icon="lucide:copy" width="16" height="16" />
                        <span className={translatedText ? "" : "vr-air-dim"}>Copy</span>
                    </button>
                    <button
                        className="vr-btn vr-btn-primary"
                        onClick={handleSaveTextFile}
                        disabled={!translatedText}
                    >
                        <Icon icon="lucide:file-down" width="16" height="16" aria-label="Save" />
                        <span className={translatedText ? "" : "vr-air-dim"}>Save text file</span>
                    </button>
                </div>
            </section>
        </div>
    );
};

export default TranslateTextLogic;
